import { matchPath } from 'react-router';
import { createSelector } from 'reselect';

import { CONVERSATION_TYPES, UI_TABS } from 'constants/ChatConstants';
import { PATH } from 'constants/RoutesConstants';
import { getCurrentLocation } from 'selectors/router';
import { uniq } from 'utils/ObjectUtils';
import { getProps } from 'utils/selectors';

import { getState, getUIState } from './general';

const getDashboardState = createSelector(getUIState, state => state.dashboard);

export const isCustomerConversationsLoading = createSelector(getDashboardState, state => state.customerConversation.isLoading);

export const isInternalConversationsLoading = createSelector(getDashboardState, state => state.internalConversation.isLoading);

export const getApiError = createSelector(getDashboardState, state => state.customerConversation.error || state.internalConversation.error);

const getAllConversations = createSelector(getState, state => state.conversations);

export const hasMoreCustomerConversation = createSelector(getDashboardState, ({ customerConversation }) => customerConversation.hasMore);

export const getCustomerConversationsIds = createSelector(getDashboardState, state => state.customerConversation.ids);

export const getCustomerConversations = createSelector(
  getAllConversations,
  getCustomerConversationsIds,
  (conversations, customerConversationIds) =>
    customerConversationIds.reduce((acc, id) => {
      if (conversations[id]) {
        acc.push(conversations[id]);
      }
      return acc;
    }, [])
);

export const getCustomerConversationsPage = createSelector(getDashboardState, state => state.customerConversation.page);

export const hasMoreInternalConversation = createSelector(getDashboardState, ({ internalConversation }) => internalConversation.hasMore);

export const getInternalConversationsIds = createSelector(getDashboardState, state => state.internalConversation.ids);

export const getInternalConversations = createSelector(
  getAllConversations,
  getInternalConversationsIds,
  (conversations, internalConversations) => internalConversations.map(id => conversations[id])
);

export const getInternalConversationsPage = createSelector(getDashboardState, state => state.internalConversation.page);

export const getSelectedConversationId = createSelector(
  getCurrentLocation,
  location => {
    const match = matchPath(PATH.CHAT.path, location);
    return match?.params?.conversationId;
  }
);

export const getSelectedConversation = createSelector(
  getAllConversations,
  getSelectedConversationId,
  (conversations, selectedConversationId) => conversations[selectedConversationId]
);

export const getConversationById = createSelector(getAllConversations, getProps, (conversations, id) => conversations[id]);

export const isClientConversation = createSelector(
  getConversationById,
  conversation => conversation?.type === CONVERSATION_TYPES.EXTERNAL
);

export const getUnreadConversationsCount = createSelector(
  getUIState,
  state => uniq([...Object.values(state.countByType ?? []).flat()]).length
);

export const getActiveTab = createSelector(getDashboardState, state => state.activeTab);

export const canShowClientConversation = createSelector(getActiveTab, tab =>
  [UI_TABS.ALL_CLIENT, UI_TABS.MY_CLIENT].includes(tab));

export const isInit = createSelector(getDashboardState, state => state?.isInit);

export const conversationIsLoading = createSelector(
  getDashboardState,
  getProps,
  (state, conversationId) => state.patching[conversationId] ?? false
);
